import axios from 'axios'
import { authService } from './authService'
import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { handleAuthExpired } from '../utils/sessionReplacedHandler'

// ฟังก์ชันสำหรับ request แบบมี auth token
const apiRequest = async (endpoint: string, options: any = {}) => {
    const token = authService.getToken()
    const user = authService.getCurrentUser()

    if (!user?.tenantId) {
        throw new Error('ไม่พบข้อมูล tenant')
    }

    // API request logging ถูกปิดเพื่อลด spam logs

    const config = {
        url: withApiBaseUrl(endpoint),
        method: options.method || 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
            'X-Tenant-ID': user.tenantId,
            ...options.headers
        },
        ...options
    }

    try {
        const response = await axios(config)
        return response.data
    } catch (error: any) {
        const status = Number(error?.response?.status || 0)
        const responseData = error?.response?.data
        const message = String(responseData?.error || responseData?.message || '').toLowerCase()
        if (
            status === 401 ||
            (status === 403 && (message.includes('invalid or expired token') || message.includes('invalid token') || message.includes('expired token')))
        ) {
            await handleAuthExpired()
        }
        console.error('❌ API Request failed:', error.response?.data || error.message)
        throw error
    }
}

export interface WalletBalance {
    main: number
    bonus: number
    pending: number
    frozen: number
}

export interface WalletData {
    userId: string
    tenantId: string
    balance: WalletBalance
    currency: string
    status: string
    limits: {
        dailyDeposit: number
        dailyWithdraw: number
        monthlyDeposit: number
        monthlyWithdraw: number
        minWithdraw: number
        minDeposit: number
    }
    statistics: {
        totalDeposits: { count: number; amount: number }
        totalWithdrawals: { count: number; amount: number }
        totalBonuses: { count: number; amount: number }
    }
    verification: {
        level: string
        kycStatus: string
    }
}

export interface WalletResponse {
    success: boolean
    data?: WalletData
    error?: string
}

export const walletService = {
    // ดึงข้อมูล wallet
    async getWallet(): Promise<WalletResponse> {
        try {
            const user = authService.getCurrentUser()
            if (!user) {
                return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
            }

            // API ใช้งานได้แล้ว - เอา debug log ออก

            // Use correct wallet API endpoint pattern
            const userRecord = user as typeof user & { _id?: string; id?: string }
            const userId = userRecord._id || userRecord.id
            const tenantId = user.tenantId

            if (!userId) {
                return { success: false, error: 'ไม่พบ User ID' }
            }

            if (!tenantId) {
                return { success: false, error: 'ไม่พบ Tenant ID' }
            }

            const response = await apiRequest(`/api/v1/wallet/tenant/${tenantId}/users/${userId}/wallet`)

            // Wallet response logging ถูกปิดเพื่อลด spam logs

            return response
        } catch (error: any) {
            console.error('❌ Get wallet failed:', error)
            return {
                success: false,
                error: error.response?.data?.error || 'ไม่สามารถดึงข้อมูล wallet ได้'
            }
        }
    },

    // ฟอร์แมตเงิน
    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('th-TH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount)
    }
}
